import { withPaletteResolvedColors } from "../../../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteFromFill";
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../../../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";
import { toAbsoluteMediaUrl } from "../../../components/ShareModal/toAbsoluteMediaUrl";

// Shared wheel-picker list helpers (attach + detach). Wheel names come back
// wrapped in rich-text HTML (e.g. "<p>notepad</p>"). Strip tags + decode the
// couple of entities we actually see so the list shows clean, readable titles.
export const stripHtml = (value) =>
  (value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .trim();

// Total segments across all sections — a cheap, unambiguous descriptor that
// helps users tell similarly-named wheels apart.
export const countSegments = (wheel) =>
  (wheel.wheel_section || []).reduce(
    (total, section) => total + (section?.activity_wheel?.length || 0),
    0
  );

export const trimUrl = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

// The backend sometimes returns wheel media URLs (wheel_preview, wheel_image)
// as a bucket-RELATIVE key with no scheme/host (measured for this exact field
// in toAbsoluteMediaUrl.js). Dropped straight into an <img src>, a relative
// key resolves against the APP's origin and 404s, so the picker's onError
// fallback fires even though the wheel has a real image. Coerce to an
// absolute URL up front; an already-absolute value passes through unchanged.
const toWheelMediaUrl = (value) => {
  const trimmed = trimUrl(value);
  return trimmed ? toAbsoluteMediaUrl(trimmed) : null;
};

// List segments often have `segment_color: null` with a fill image. createPie's
// legacy path paints `pattern` even when the arc-fills flag is off; without
// copying the URL, those slices render as empty gray and the iYuga center
// never appears because chartData is non-empty.
export const segmentFillUrl = (seg) => {
  const fill = seg?.fill;
  if (!fill || typeof fill !== "object") return null;
  if (fill.type === "gif") return trimUrl(fill.gif?.url);
  if (fill.type === "image") return trimUrl(fill.image?.url);
  return trimUrl(fill.image?.url) || trimUrl(fill.gif?.url);
};

// Picker thumbs always use live D3 (WheelComponent). `pickerChartData`
// flattens every section's activity_wheel for that render. Top-level `color`
// on list segments is often the brand default orange (#F79A00); the real slice
// color lives on `settings` (and palette primary when that flag is on).
// Gallery WheelCard runs withPaletteResolvedColors before WheelComponent;
// without that step the D3 thumb would look orange.
//
// `wheel_preview` is still normalized onto `image` for any caller that wants
// the snapshot URL; PickerWheelThumb ignores it and always paints D3.
export const pickerChartData = (wheel) => {
  const sections = Array.isArray(wheel?.wheel_section) ? wheel.wheel_section : [];
  const segments = sections.flatMap((section) =>
    Array.isArray(section?.activity_wheel) ? section.activity_wheel : []
  );
  const mapped = segments
    .map((seg) => {
      const fillUrl = segmentFillUrl(seg);
      return {
        label: "blank",
        value: "1",
        isBlank: false,
        ...seg,
        ...(fillUrl && !seg.pattern ? { pattern: fillUrl } : {}),
      };
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  return withPaletteResolvedColors(mapped, {
    enabled: SEGMENT_PALETTE_COLOR_ENABLED,
  });
};

export const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const normalizeWheels = (data, { filter } = {}) => {
  const raw = data?.data ?? data ?? {};
  const list = Array.isArray(raw) ? raw : raw.results ?? raw.data ?? [];
  const source = Array.isArray(list) ? list : [];
  const filtered = typeof filter === "function" ? source.filter(filter) : source;
  return filtered.map((w) => {
    const segments = countSegments(w);
    const createdAt = formatDate(w.created_at);
    const meta = [
      segments > 0 ? `${segments} segment${segments === 1 ? "" : "s"}` : null,
      createdAt || null,
    ]
      .filter(Boolean)
      .join(" · ");
    return {
      id: w.id,
      name: stripHtml(w.title || w.name || w.wheel_name) || "Untitled wheel",
      // Snapshot URL (optional). Empty / null `wheel_preview` must not fall
      // through to `wheel_image` (that's the center portrait). Thumbs use D3.
      image: toWheelMediaUrl(w.wheel_preview) || toWheelMediaUrl(w.preview_image) || null,
      portrait: toWheelMediaUrl(w.wheel_image) || null,
      chartData: pickerChartData(w),
      meta,
      // Raw timestamp (not the display-formatted `meta` string) so "Newest
      // first" can actually compare dates instead of comparing text.
      createdAt: w.created_at || null,
    };
  });
};

export const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "recent", label: "Newest first" },
];

// BUG-capture-f24d6e — the picker used to be a flat, unfiltered list; with
// enough wheels, finding the right one meant scrolling through all of them.
// Search narrows by name; sort reorders what search leaves behind, so the
// two compose instead of fighting each other.
export const applySearchAndSort = (wheels, search, sortBy) => {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? wheels.filter((w) => w.name.toLowerCase().includes(q))
    : wheels;
  const sorted = [...filtered];
  if (sortBy === "recent") {
    sorted.sort((a, b) => {
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  } else {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  }
  return sorted;
};
